import { ArrowDropDown, ArrowRight } from "@mui/icons-material";
import { Box, FormControlLabel, Tooltip } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { JobColor } from "../../../../../export/jobs";
import Highlight from "../../../../components/Highlight";
import FilterListCheckbox from "./filter-list-checkbox";

// This item represents a row in the virtual list
export const HierarchicalListItem: React.FC<any> = ({ 
  index, 
  style, 
  data 
}) => {
  const { 
    flattenedItems, 
    isChecked, 
    searchedValue, 
    listWidth, 
    applyFilter, 
    toggleExpand,
    expandedItems
  } = data;
  
  const item = flattenedItems[index];
  const textRef = useRef<HTMLSpanElement>(null);
  const [isEllipsed, setIsEllipsed] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedItems.includes(item.id);
  const indentLevel = item.level || 0;
  const hasParent = item.parentId !== undefined;
  
  useEffect(() => {
    if (textRef.current) {
      setIsEllipsed(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [item.name]);
  
  return (
    <div
      style={{
        ...style,
        width: `${listWidth}px`,
        overflowX: "hidden",
        boxSizing: "border-box",
        // Nesting indent lives here (not on the FormControlLabel) so top-level
        // rows keep the same default left margin as the flat rows and the
        // "All …" select-all row, keeping every checkbox aligned.
        paddingLeft: (indentLevel > 0 || hasParent)
          ? `${(indentLevel > 0 ? indentLevel : 1) * 28}px`
          : 0,
      }}
    >
      <FormControlLabel
        key={`filter-${item.id}`}
        sx={{
          width: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          // No marginLeft override: inherit the same default as the flat rows
          // and the "All …" select-all row so checkboxes align. Nesting indent
          // is handled by the row wrapper's paddingLeft.
          marginTop: 0,
          marginBottom: 0,
          marginRight: 0,
          padding: 0,
          height: "30px",
          display: "flex",
          alignItems: "center",
          backgroundColor: "transparent",
        }}
        control={
          <FilterListCheckbox
            checked={isChecked(item.id)}
            onChange={(e) => {
              applyFilter(item.id, e.target.checked);
            }}
          />
        }
        label={
          <Box display="flex" alignItems="center" height="30px">
            {hasChildren && (
              <Box
                component="span"
                sx={{
                  width: "16px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ArrowDropDown
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleExpand(item.id);
                  }}
                  sx={{
                    cursor: "pointer",
                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 150ms ease",
                    fontSize: "18px",
                  }}
                />
              </Box>
            )}

            {!hasChildren && (
              <Box
                component="span"
                sx={{
                  width: "16px",
                  height: "30px",
                }}
              />
            )}
            
            {item.hexColor && (
              <JobColor color={`#${item.hexColor}`} />
            )}
            
            <Tooltip title={isEllipsed ? item.name : null}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  fontSize: "14px",
                  marginLeft: item.hexColor ? "12px" : "2px",
                  fontFamily: "Roboto",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: `${
                    item.hexColor 
                      ? listWidth - 106 - ((indentLevel > 0 || hasParent) ? (indentLevel > 0 ? indentLevel : 1) * 28 : 0)
                      : listWidth - 80 - ((indentLevel > 0 || hasParent) ? (indentLevel > 0 ? indentLevel : 1) * 28 : 0)
                  }px`,
                  position: "relative",
                }}
              >
                <Box
                  ref={textRef}
                  component="span"
                  sx={{
                    display: "inline-block",
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    transition: "max-width 0.5s ease",
                  }}
                >
                  <Highlight
                    value={item.name}
                    searchText={searchedValue}
                  />
                </Box>
              </Box>
            </Tooltip>
          </Box>
        }
      />
    </div>
  );
};

// Function to flatten hierarchical data for virtual list rendering
export const flattenHierarchy = (
  items: any[], 
  expandedItems: number[], 
  level = 0,
  visited: Set<number> = new Set()
): any[] => {
  return items.reduce((acc: any[], item: any) => {
    if (visited.has(item.id)) return acc;
    visited.add(item.id);
    
    // Add the current item with its level
    const itemWithLevel = { ...item, level };
    acc.push(itemWithLevel);
    
    // If item has children and is expanded, add children recursively
    if (
      item.children &&
      item.children.length > 0 &&
      expandedItems.includes(item.id)
    ) {
      acc.push(...flattenHierarchy(item.children, expandedItems, level + 1, visited));
    }
    
    return acc;
  }, []);
};
